import { Store, configureStore } from "@reduxjs/toolkit";
import {
    FLUSH,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
    REHYDRATE,
    persistStore,
} from "redux-persist";
import { guess as guessLocation } from "tsi-common-react/src/api/location";
import { listenerMiddleware } from "tsi-common-react/src/apps/common/listeners";
import { updateDetectedLocation } from "tsi-common-react/src/apps/common/reducers";
import {
    viewStackPush,
    viewStackReplaceTop,
    viewStackSetRoot,
} from "tsi-common-react/src/apps/csr/reducers";
import { middleware } from "tsi-common-react/src/apps/middleware";

import { reducers } from "./reducers";
import { IReduxState } from "./reducers.interfaces";

type PromiseResolve<T> = (value: T | PromiseLike<T>) => void;
type PromiseReject<T> = (reason: T | PromiseLike<T>) => void;

const newDefer = <T>() => {
    let resolve: PromiseResolve<T> = () => null;
    let reject: PromiseReject<T> = () => null;
    const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return {
        resolve: resolve,
        reject: reject,
        promise: promise,
    };
};
const _rehydratingStore = newDefer<Store<IReduxState>>();
export const rehydratingStore = _rehydratingStore.promise;

export const store = configureStore({
    reducer: reducers,
    middleware: (getDefaultMiddleware) => {
        return getDefaultMiddleware({
            immutableCheck: {
                ignoredPaths: [
                    /^reviews\.data\.products\.([0-9]+)/,
                    /^checkout\.data\.payment_methods\.([0-9]+)/,
                ],
            },
            serializableCheck: {
                ignoredPaths: [
                    /^reviews\.ui\.writeReviewForms\.([a-zA-Z0-9-]+)\.started/,
                    /^csr\.views\.stack\.(\d+)\.component/,
                ],
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                    viewStackSetRoot.type,
                    viewStackPush.type,
                    viewStackReplaceTop.type,
                ],
            },
        })
            .prepend(listenerMiddleware.middleware)
            .concat(middleware);
    },
    devTools: {
        name: "Global",
    },
});

export const persistor = persistStore(store, null, () => {
    _rehydratingStore.resolve(store);
});

rehydratingStore
    .then(async (_store) => {
        const state = _store.getState();
        if (!state.common.detectedLocation) {
            const location = await guessLocation();
            if (location) {
                store.dispatch(updateDetectedLocation(location));
            }
        }
    })
    .catch(console.error);
